"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Dialog from "../components/Dialog";
import PublicationSpacingDialog from "../components/PublicationSpacingDialog";
import { findCloselySpacedPublications, type PublicationSpacingPost } from "../components/publicationFlow/publicationSpacing";
import type { SchedulingBatchRequest } from "../components/publicationFlow/schedulingBatch";
import type { PublicationModel } from "../types/models";
import type { SocialProviderAccount } from "./useSocialProviderAccounts";

type PendingSpacing = {
  ownerId: string | undefined;
  posts: PublicationSpacingPost[];
  accounts: readonly SocialProviderAccount[];
  publishNow: boolean;
};
type SpacingDecision = "proceed" | "change-time" | "cancel";

export function usePublicationSpacingWarning(ownerId: string | undefined) {
  const [pending, setPending] = useState<PendingSpacing | null>(null);
  const resolveRef = useRef<((decision: SpacingDecision) => void) | null>(null);
  useEffect(() => () => {
    resolveRef.current?.("cancel");
    resolveRef.current = null;
  }, [ownerId]);

  const confirmSpacing = useCallback(({ request, publications, accounts, publishNow }: {
    request: SchedulingBatchRequest;
    publications: readonly PublicationModel[];
    accounts: readonly SocialProviderAccount[];
    publishNow: boolean;
  }): Promise<SpacingDecision> => {
    if (resolveRef.current) return Promise.resolve("cancel");
    const posts = findCloselySpacedPublications(request, publications);
    if (!posts.length) return Promise.resolve("proceed");
    return new Promise(resolve => {
      resolveRef.current = resolve;
      setPending({ ownerId, posts, accounts, publishNow });
    });
  }, [ownerId]);

  const finish = (decision: SpacingDecision) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setPending(null);
    resolve?.(decision);
  };
  const isOpen = pending !== null && pending.ownerId === ownerId;
  return {
    confirmSpacing,
    isOpen,
    dialog: <Dialog isOpen={isOpen} onClose={() => finish("cancel")} overlayClassName="!z-[100001]" className="max-w-md">
      {isOpen && <PublicationSpacingDialog {...pending} onConfirm={() => finish("proceed")} onCancel={() => finish("change-time")} />}
    </Dialog>,
  };
}
